import React, { useEffect, useState } from "react";
import { api } from "@/lib/caesar";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, SectionLabel, Labeled } from "@/components/common";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Calendar, Mail, Contact, HardDrive, Instagram, Facebook, Youtube, Linkedin, MessageCircle, BarChart3, Newspaper, Music2, Plus, UserX } from "lucide-react";

const CONNECTIONS = [
  { key: "gcal", label: "Google Calendar", icon: Calendar, note: "Phase One priority — OAuth wiring next." },
  { key: "gmail", label: "Gmail", icon: Mail }, { key: "gcontacts", label: "Google Contacts", icon: Contact },
  { key: "gdrive", label: "Google Drive", icon: HardDrive }, { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "facebook", label: "Facebook", icon: Facebook }, { key: "tiktok", label: "TikTok", icon: Music2 },
  { key: "youtube", label: "YouTube", icon: Youtube }, { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle }, { key: "analytics", label: "Website Analytics", icon: BarChart3 },
  { key: "news", label: "News Sources", icon: Newspaper },
];

export default function Connections() {
  const { user, logout } = useAuth();
  const admin = user?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState({ daily: 0, weekly: 0, monthly: 0, annual: 0 });
  const [invite, setInvite] = useState({ email: "", name: "", role: "team_member", password: "" });

  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { if (admin) loadUsers(); api.get("/goals").then(({ data }) => setGoals(data)).catch(() => {}); }, [admin]);

  const saveGoals = async () => { await api.put("/goals", goals); toast.success("Goals saved"); };
  const setRole = async (u, role) => { await api.put(`/admin/users/${u.user_id}`, { role }); loadUsers(); };
  const toggleActive = async (u) => { await api.put(`/admin/users/${u.user_id}`, { active: !u.active }); loadUsers(); };
  const addUser = async () => {
    if (!invite.email) { toast.error("Email required"); return; }
    try { await api.post("/admin/users", invite); toast.success("User invited"); setInvite({ email: "", name: "", role: "team_member", password: "" }); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Settings & Connections" subtitle="Integrations, goals, and command personnel" testId="connections-header" />

      {admin && (
        <div>
          <SectionLabel>Revenue Goals</SectionLabel>
          <Panel className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {["daily", "weekly", "monthly", "annual"].map((k) => (
              <Labeled key={k} label={k}><Input data-testid={`goal-${k}`} type="number" value={goals[k] ?? 0} onChange={(e) => setGoals({ ...goals, [k]: e.target.value })} className="bg-ink border-edge font-num" /></Labeled>
            ))}
            <div className="col-span-2 sm:col-span-4"><button data-testid="goals-save" onClick={saveGoals} className="px-5 h-10 rounded-lg bg-gold text-ink font-bold">Save Goals</button></div>
          </Panel>
        </div>
      )}

      <div>
        <SectionLabel>Connections</SectionLabel>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CONNECTIONS.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} data-testid={`connection-${c.key}`} className="bg-surface border border-edge rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-ink border border-edge grid place-items-center"><Icon size={16} className="text-[#A3A3A3]" /></div><span className="font-semibold text-sm">{c.label}</span></div>
                  <span className="text-[0.6rem] font-bold tracking-wider px-2 py-0.5 rounded-full border border-edge text-[#A3A3A3]">FUTURE</span>
                </div>
                <p className="text-xs text-[#5a5a5a] mt-3">{c.note || "Not connected · prepared for a future release."}</p>
              </div>
            );
          })}
        </div>
      </div>

      {admin && (
        <div>
          <SectionLabel>Command Personnel</SectionLabel>
          <Panel className="divide-y divide-edge/50">
            {users.map((u) => (
              <div key={u.user_id} data-testid={`user-${u.user_id}`} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0"><p className="font-semibold text-sm truncate">{u.name}{u.active === false && <span className="text-sred text-xs ml-2">disabled</span>}</p><p className="text-xs text-[#A3A3A3] truncate">{u.email} · last login {u.last_login ? new Date(u.last_login).toLocaleDateString() : "never"}</p></div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className="h-9 rounded-md bg-ink border border-edge px-2 text-sm"><option value="super_admin">Super Admin</option><option value="team_member">Team Member</option></select>
                  <button onClick={() => toggleActive(u)} className="text-[#A3A3A3] hover:text-sred p-1" title="Toggle active"><UserX size={16} /></button>
                </div>
              </div>
            ))}
          </Panel>
          <Panel className="p-4 mt-3">
            <SectionLabel>Invite Team Member</SectionLabel>
            <div className="grid sm:grid-cols-4 gap-3">
              <Input data-testid="invite-email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="Email" className="bg-ink border-edge" />
              <Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} placeholder="Name" className="bg-ink border-edge" />
              <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="h-10 rounded-md bg-ink border border-edge px-2 text-sm"><option value="team_member">Team Member</option><option value="super_admin">Super Admin</option></select>
              <Input data-testid="invite-password" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} placeholder="Temp password" className="bg-ink border-edge" />
            </div>
            <button data-testid="invite-save" onClick={addUser} className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold"><Plus size={15} /> Invite User</button>
          </Panel>
        </div>
      )}

      <div>
        <SectionLabel>Session</SectionLabel>
        <Panel className="p-4 flex items-center justify-between">
          <div><p className="font-semibold text-sm">{user?.name}</p><p className="text-xs text-[#A3A3A3]">{user?.email} · {user?.role}</p></div>
          <button data-testid="settings-logout" onClick={logout} className="px-4 h-9 rounded-lg border border-sred/40 text-sred text-sm font-semibold hover:bg-sred/10 transition-colors">Log Out</button>
        </Panel>
      </div>
    </div>
  );
}
